import moment from "moment";
import { ObjectSchema, array, mixed, number, object } from "yup";
import {
  numberDecimal,
  numberInteger,
  requiredBoolean,
  requiredClassifierId,
  requiredString,
  simpleString,
} from "../../../../../data/yupConstants";
import { SubmitBlankMediaImporterApplication } from "../../../../../generated/data-contracts";
import { RateType, ResCallback } from "../../../../../models";

const validationSectionCountries = (res: ResCallback) =>
  object().shape({
    blankMediaTypeImporterCountryId: number(),
    equipmentId: requiredClassifierId(res),
    cnCode: simpleString(res, undefined, 20),
    countryRegionId: requiredClassifierId(res),
    count: numberInteger(res, true, true),
  });
const validationSectionExpropriations = (res: ResCallback) => {
  return object().shape({
    blankMediaTypeImporterExpropriationId: number(),
    equipmentId: requiredClassifierId(res),
    cnCode: simpleString(res, undefined, 200),
    price: number().when(["amount"], {
      is: (amount: number | undefined) => !amount,
      then: () => numberDecimal(res, true, true),
    }),
    count: number().when(["amount"], {
      is: (amount: number | undefined) => !amount,
      then: () => numberDecimal(res, true, true),
    }),
    amount: numberDecimal(res, true, true),
  });
};
const validationSectionManufEquipments = (res: ResCallback) => {
  return object().shape({
    blankMediaTypeImporterManufEquipmentId: number(),
    equipmentId: requiredClassifierId(res),
    cnCode: simpleString(res, undefined, 200),
    count: numberInteger(res, true, true),
  });
};
const validationSectionSales = (res: ResCallback) => {
  return object().shape({
    blankMediaTypeImporterSaleId: number(),
    equipmentId: requiredClassifierId(res),
    cnCode: simpleString(res, undefined, 200),
    personBlankMediaUse: requiredString(res),
    personId: requiredClassifierId(res),
    count: numberInteger(res, true, true),
    transactionDate: requiredString(res).test(
      "InParentPeriod",
      res("validation_DateOutsidePeriodBoundaries"),
      (value, context) => {
        const contextParentObject = context.options.context as any;
        const dateFrom = moment(contextParentObject.dateFrom);
        const dateTo = moment(contextParentObject.dateTo).endOf("day");
        const checkDate = moment(value);

        return (
          checkDate.isSameOrAfter(dateFrom) && checkDate.isSameOrBefore(dateTo)
        );
      },
    ),
  });
};
const validationSectionSummaries = (res: ResCallback) => {
  return object().shape({
    blankMediaTypeImporterSummaryId: number(),
    equipmentId: requiredClassifierId(res),
    cnCode: simpleString(res, undefined, 200),
    imported: number(),
    exported: number(),
    sold: number(),
    balance: number(),
    expropriationAmount: number(),
    rateType: mixed<RateType>(),
    rate: number(),
    paymentAmount: number(),
  });
};
export const validationSchemaBlankMediaImporterApplication = (
  res: ResCallback,
  acceptedNoDeals: boolean,
): ObjectSchema<SubmitBlankMediaImporterApplication> | undefined => {
  const objectShapeCountries = validationSectionCountries(res);
  const validationSchema: ObjectSchema<SubmitBlankMediaImporterApplication> =
    object({
      blankMediaTypeId: number(),
      dealNotOccuredPortal: requiredBoolean(res).test(
        "NoDealAcceptNeeded",
        res("validation_ApproveRequired"),
        (value) => {
          if (value == acceptedNoDeals) return true;
        },
      ),
      submitReport: requiredBoolean(res),
      dateFrom: requiredString(res),
      dateTo: requiredString(res),
      importedFromCountries: array().when(["dealNotOccuredPortal"], {
        is: (dealNotOccuredPortal: boolean) => dealNotOccuredPortal === false,
        then: () => array().of(objectShapeCountries),
      }),
      exportedToCountries: array().when(["dealNotOccuredPortal"], {
        is: (dealNotOccuredPortal: boolean) => dealNotOccuredPortal === false,
        then: () => array().of(objectShapeCountries),
      }),
      expropriations: array().when(["dealNotOccuredPortal"], {
        is: (dealNotOccuredPortal: boolean) => dealNotOccuredPortal === false,
        then: () => array().of(validationSectionExpropriations(res)),
      }),
      manufEquipments: array().when(["dealNotOccuredPortal"], {
        is: (dealNotOccuredPortal: boolean) => dealNotOccuredPortal === false,
        then: () => array().of(validationSectionManufEquipments(res)),
      }),
      sales: array().when(["dealNotOccuredPortal"], {
        is: (dealNotOccuredPortal: boolean) => dealNotOccuredPortal === false,
        then: () => array().of(validationSectionSales(res)),
      }),
      summaries: array().when(["dealNotOccuredPortal"], {
        is: (dealNotOccuredPortal: boolean) => dealNotOccuredPortal === false,
        then: () => array().of(validationSectionSummaries(res)),
      }),
    });

  return validationSchema;
};
